import { Box, Button, IconButton, Stack, Tooltip, useMediaQuery, useTheme } from '@mui/material'
import MyLocationIcon from '@mui/icons-material/MyLocation'
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap'
import { useMap } from 'react-leaflet'
import L from 'leaflet'
import type { HeatmapPoint } from '@api/endpoints/marketing'
import type { Location } from '@hooks/useGeolocation'

interface MapViewControlsProps {
  /** Every point across the dataset, for the fit action. */
  points: HeatmapPoint[]
  location: Location | null
  zoom?: number
}

/**
 * Two deliberate ways to move the map: to the data, or to you.
 *
 * Fitting to the data used to happen on its own after every query. On a catalogue that spans the
 * country that produces a continental view in which no individual cell is distinguishable from any
 * other — technically the whole dataset, practically unreadable — and it overrode wherever the
 * operator had navigated. It is still the right thing to want sometimes, so it is still here; it
 * just waits to be asked now.
 */
export function MapViewControls({ points, location, zoom = 11 }: MapViewControlsProps) {
  const map = useMap()
  const theme = useTheme()
  const compact = useMediaQuery(theme.breakpoints.down('sm'))

  const fitToData = () => {
    if (points.length === 0) return
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng] as [number, number]))
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 })
    }
  }

  const goToMe = () => {
    if (!location) return
    map.setView([location.latitude, location.longitude], zoom)
  }

  const fitHint = points.length === 0 ? 'No data in this window' : 'Zoom out to the whole dataset'
  const meHint =
    location == null
      ? 'Your location is not available'
      : location.source === 'gps'
        ? 'Centre on your current position'
        : 'Centre on your home location'

  const buttonSx = { bgcolor: 'background.paper', color: 'text.primary', boxShadow: 2 }

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 10,
        right: 10,
        // Above Leaflet's own panes, which sit in the 400s and would otherwise swallow the clicks.
        zIndex: 1000,
      }}
    >
      {/* On a phone the map is only a few hundred px wide, and two labelled buttons covered a
          third of it, so they shrink to stacked icon buttons there. */}
      <Stack direction={compact ? 'column' : 'row'} spacing={1}>
        <Tooltip title={fitHint}>
          {/* A disabled button cannot fire the events a Tooltip listens for, so it needs a wrapper
              that can — otherwise the explanation never appears in exactly the case that needs it. */}
          <span>
            {compact ? (
              <IconButton aria-label="Fit to data" onClick={fitToData} disabled={points.length === 0} sx={buttonSx}>
                <ZoomOutMapIcon />
              </IconButton>
            ) : (
              <Button
                size="small"
                variant="contained"
                color="inherit"
                startIcon={<ZoomOutMapIcon />}
                onClick={fitToData}
                disabled={points.length === 0}
                sx={buttonSx}
              >
                Fit to data
              </Button>
            )}
          </span>
        </Tooltip>

        <Tooltip title={meHint}>
          <span>
            {compact ? (
              <IconButton aria-label="My location" onClick={goToMe} disabled={location == null} sx={buttonSx}>
                <MyLocationIcon />
              </IconButton>
            ) : (
              <Button
                size="small"
                variant="contained"
                color="inherit"
                startIcon={<MyLocationIcon />}
                onClick={goToMe}
                disabled={location == null}
                sx={buttonSx}
              >
                My location
              </Button>
            )}
          </span>
        </Tooltip>
      </Stack>
    </Box>
  )
}
